import {
  LIMIT_FIELDS,
  LimitDomain,
  LimitDomains,
  LimitField,
  LimitIssue,
  LimitMetricSpec,
  LimitResult,
  LimitValues,
} from './types';
import {
  admissibleDomainEnds,
  containsLimitValue,
  finiteDecimal,
  parseNumericInterval,
} from './interval';

export function normalizeCelsiusUnit(unit: unknown): 'C' | undefined {
  if (typeof unit !== 'string') return undefined;
  return ['c', '°c', 'celsius', 'degrees_c', 'degrees_celsius'].includes(
    unit.trim().toLowerCase(),
  )
    ? 'C'
    : undefined;
}
export function resolveLimitDomains(
  specs: Readonly<Partial<Record<LimitField, LimitMetricSpec>>>,
): LimitResult<LimitDomains> {
  const issues: LimitIssue[] = [];
  const domains = {} as Record<LimitField, LimitDomain>;
  for (const field of LIMIT_FIELDS) {
    const spec = specs?.[field];
    if (!spec || typeof spec !== 'object') {
      issues.push({
        code: 'MISSING_DOMAIN',
        field,
        message: 'Limit metric specification is unavailable.',
      });
      continue;
    }
    const units = [spec.units, spec.unit].filter((unit) => unit !== undefined);
    if (!units.length || units.some((unit) => !normalizeCelsiusUnit(unit))) {
      issues.push({
        code: 'INVALID_UNITS',
        field,
        message: 'Limit domain must use documented Celsius units.',
      });
      continue;
    }
    // Existing specs may omit the type; an explicit nonnumeric type is never parsed as an interval.
    if (
      spec.valueType !== undefined &&
      (typeof spec.valueType !== 'string' ||
        !['number', 'float', 'integer', 'double', 'numeric'].includes(
          spec.valueType.toLowerCase(),
        ))
    ) {
      issues.push({
        code: 'INVALID_VALUE_TYPE',
        field,
        message: 'Limit metric must be numeric.',
      });
      continue;
    }
    if (spec.readOnly !== undefined && typeof spec.readOnly !== 'boolean') {
      issues.push({
        code: 'INVALID_READ_ONLY',
        field,
        message: 'Read-only metadata must be boolean.',
      });
      continue;
    }
    if (spec.readOnly === true) {
      issues.push({
        code: 'READ_ONLY',
        field,
        message: 'Limit metric is read-only.',
      });
      continue;
    }
    const parsed = parseNumericInterval(spec.value);
    if (!parsed.ok) {
      issues.push(...parsed.issues.map((issue) => ({ ...issue, field })));
      continue;
    }
    if (
      spec.valueStep !== undefined &&
      (typeof spec.valueStep !== 'number' ||
        !Number.isFinite(spec.valueStep) ||
        spec.valueStep <= 0)
    ) {
      issues.push({
        code: 'INVALID_STEP',
        field,
        message: 'Limit step must be finite and positive.',
      });
      continue;
    }
    const domain: {
      interval: typeof parsed.value;
      step?: number;
      defaultValue?: number;
    } = { interval: parsed.value };
    if (spec.valueStep !== undefined) domain.step = spec.valueStep;
    if (domain.step !== undefined) {
      const ends = admissibleDomainEnds(domain);
      if (ends.minimum === undefined || ends.maximum === undefined) {
        issues.push({
          code: 'NO_GRID_VALUE',
          field,
          message: 'Domain has no representable zero-Celsius grid member.',
        });
        continue;
      }
    }
    if (spec.defaultValue !== undefined && spec.defaultValue !== null) {
      const value = finiteDecimal(spec.defaultValue);
      if (value === undefined || !containsLimitValue(value, domain)) {
        issues.push({
          code: 'INVALID_DEFAULT',
          field,
          message: 'Published default must belong to its domain and grid.',
        });
        continue;
      }
      domain.defaultValue = value;
    }
    domains[field] = Object.freeze(domain);
  }
  return issues.length
    ? { ok: false, issues: Object.freeze(issues) }
    : { ok: true, value: Object.freeze(domains) };
}
export function validateLimitValues(
  values: LimitValues,
  domains: LimitDomains,
): readonly LimitIssue[] {
  const issues: LimitIssue[] = [];
  for (const field of LIMIT_FIELDS) {
    if (
      typeof values?.[field] !== 'number' ||
      !domains?.[field] ||
      !containsLimitValue(values[field], domains[field])
    ) {
      issues.push({
        code: 'OUTSIDE_DOMAIN',
        field,
        message: 'Configured limit is outside its admissible domain.',
      });
    }
  }
  if (values.minHeatSetpointLimit > values.maxHeatSetpointLimit)
    issues.push({
      code: 'HEAT_ORDER',
      message: 'Heating minimum exceeds heating maximum.',
    });
  if (values.minCoolSetpointLimit > values.maxCoolSetpointLimit)
    issues.push({
      code: 'COOL_ORDER',
      message: 'Cooling minimum exceeds cooling maximum.',
    });
  return Object.freeze(issues);
}
export function mergeAndValidateLimitValues(
  current: LimitValues,
  patch: Readonly<Partial<LimitValues>>,
  domains: LimitDomains,
): LimitResult<LimitValues> {
  const values = Object.freeze({ ...current, ...patch });
  const issues = validateLimitValues(values, domains);
  return issues.length ? { ok: false, issues } : { ok: true, value: values };
}
